import styles from './Spinner.module.css';

/**
 * Central loading spinner used across the app wherever we wait for data to
 * load. Defined once here so the animation, colors, and accessibility
 * behavior stay identical everywhere (Analysis chart, Screener, Market
 * Health, Trending panel, valuation card).
 *
 * Renders an `role="status"` region so screen readers announce it, with the
 * visible `label` (or a default "Loading…") doubling as the accessible text.
 * Honors `prefers-reduced-motion` via CSS.
 *
 * @param {Object} props
 * @param {'sm'|'md'|'lg'} [props.size='md'] - Spinner diameter preset.
 * @param {string} [props.label='Loading…'] - Text shown next to / under the
 *   spinner and used as its accessible label. Pass `null`/'' to hide the
 *   visible text (an sr-only fallback still announces "Loading").
 * @param {boolean} [props.block=false] - Center the spinner (and label) as a
 *   stacked block, for full-panel loading states. Inline by default.
 * @param {string} [props.className] - Extra class on the wrapper.
 */
const Spinner = ({ size = 'md', label = 'Loading…', block = false, className = '' }) => {
  const wrapClass = [
    block ? styles.block : styles.wrap,
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapClass} role="status" aria-live="polite">
      <span
        className={`${styles.spinner} ${styles[size] || styles.md}`}
        aria-hidden="true"
      />
      {label
        ? <span className={styles.label}>{label}</span>
        : <span className={styles.srOnly}>Loading</span>}
    </div>
  );
};

export default Spinner;
